import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from './ui/card';
import { Button } from './ui/button';
import {
  ChevronDown,
  ChevronRight,
  Trash2,
  Search,
  RefreshCw,
  UserPlus,
  RefreshCcw
} from 'lucide-react';
import { getAllUsers, updateUserRole, deleteUser, getAllRoles, resetUserPassword, createUser } from '../api';

// Función para crear un toast de confirmación
const confirmToast = (message, onConfirm) => {
  return toast(
    <div>
      <p className="mb-4">{message}</p>
      <div className="flex justify-end gap-2">
        <button 
          className="px-3 py-1 bg-gray-300 text-gray-800 rounded"
          onClick={() => toast.dismiss()}
        >
          Cancelar
        </button>
        <button 
          className="px-3 py-1 bg-red-600 text-white rounded"
          onClick={() => {
            toast.dismiss();
            onConfirm();
          }}
        >
          Eliminar
        </button>
      </div>
    </div>,
    {
      position: "top-center",
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
      hideProgressBar: true,
    }
  );
};

const UserAdministration = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  // Cargar usuarios
  useEffect(() => {
    fetchRoles();
  }, []);

  // Filtrar usuarios cuando cambia la búsqueda
  useEffect(() => {
    if (searchValue.trim() === '') {
      setFilteredUsers(users);
    } else {
      const lowerCaseSearch = searchValue.toLowerCase();
      const filtered = users.filter(
        user => 
          user.name.toLowerCase().includes(lowerCaseSearch) || 
          user.email.toLowerCase().includes(lowerCaseSearch) ||
          user.role.toLowerCase().includes(lowerCaseSearch)
      );
      setFilteredUsers(filtered);
    }
  }, [searchValue, users]);

  // Función para cargar usuarios
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAllUsers();
      
      // Filtrar usuarios basándose en los roles disponibles
      const validRoles = availableRoles.map(role => role.name);
      const filteredData = data.filter(user => {
        // Incluir usuarios con roles básicos (admin, user, client) si no están en la lista de roles
        const basicRoles = ['admin', 'user', 'client'];
        if (basicRoles.includes(user.role)) {
          return true;
        }
        // Solo incluir usuarios con roles que existan en la lista de roles disponibles
        return validRoles.includes(user.role);
      });
      
      setUsers(filteredData);
      setFilteredUsers(filteredData);
      
      // Inicializar estados expandidos para roles
      const roles = [...new Set(filteredData.map(user => user.role))];
      const initialExpanded = {};
      roles.forEach(role => {
        initialExpanded[role] = true;  // Todos expandidos por defecto
      });
      setExpandedRoles(initialExpanded);
    } catch (error) {
      toast.error('Error al cargar la lista de usuarios');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar roles disponibles
  const fetchRoles = async () => {
    try {
      const roles = await getAllRoles();
      if (roles && Array.isArray(roles)) {
        setAvailableRoles(roles);
        console.log('Roles cargados desde la API:', roles);
        // Recargar usuarios después de obtener los roles para aplicar el filtrado
        fetchUsers();
      } else {
        console.warn('No se pudieron cargar los roles, usando valores predeterminados');
        // Si no se pueden cargar los roles, cargar usuarios sin filtrado
        fetchUsers();
      }
    } catch (error) {
      console.error('Error al cargar roles:', error);
      // En caso de error, cargar usuarios sin filtrado
      fetchUsers();
    }
  };

  // Función para actualizar el rol de un usuario
  const handleRoleUpdate = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success('Rol actualizado correctamente');
      fetchRoles(); // Recargar roles y usuarios
    } catch (error) {
      toast.error('Error al actualizar el rol');
      console.error(error);
    }
  };

  // Función para eliminar un usuario
  const handleDeleteUser = async (userId, userName) => {
    confirmToast(`¿Estás seguro de que deseas eliminar al usuario ${userName}?`, async () => {
      try {
        setLoading(true);
        await deleteUser(userId);
        toast.success('Usuario eliminado correctamente');
        fetchRoles(); // Recargar roles y usuarios
      } catch (error) {
        toast.error('Error al eliminar el usuario');
        console.error(error);
      } finally {
        setLoading(false);
      }
    });
  };

  // Función para resetear la contraseña de un usuario
  const handleResetPassword = async (userId, userName) => {
    const confirmMessage = (
      <div>
        <p className="mb-2">¿Estás seguro de que deseas resetear la contraseña para el usuario <span className="font-bold">{userName}</span>?</p>
        <p className="text-sm text-yellow-600">Esta acción generará una nueva contraseña aleatoria y sobrescribirá la contraseña actual.</p>
      </div>
    );
    
    toast(
      <div>
        {confirmMessage}
        <div className="flex justify-end gap-2 mt-4">
          <button 
            className="px-3 py-1 bg-gray-300 text-gray-800 rounded"
            onClick={() => toast.dismiss()}
          >
            Cancelar
          </button>
          <button 
            className="px-3 py-1 bg-yellow-600 text-white rounded"
            onClick={async () => {
              toast.dismiss();
              try {
                setLoading(true);
                const result = await resetUserPassword(userId);
                
                // Mostrar la nueva contraseña en una notificación
                toast.success(
                  <div>
                    <p className="font-bold mb-1">Contraseña reseteada correctamente</p>
                    <p className="mb-1">Usuario: <span className="font-bold">{userName}</span></p>
                    <div className="mt-2 p-2 bg-gray-100 border rounded flex items-center justify-between">
                      <code className="text-sm font-mono">{result.newPassword}</code>
                      <button
                        className="ml-2 text-blue-500 hover:text-blue-700"
                        onClick={() => {
                          navigator.clipboard.writeText(result.newPassword);
                          toast.info('Contraseña copiada al portapapeles', {
                            autoClose: 2000,
                            position: "bottom-center"
                          });
                        }}
                      >
                        Copiar
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      * Guarda esta contraseña en un lugar seguro. No se volverá a mostrar.
                    </p>
                  </div>,
                  {
                    autoClose: false,
                    closeButton: true
                  }
                );
                
                fetchRoles(); // Recargar roles y usuarios
              } catch (error) {
                toast.error('Error al resetear la contraseña');
                console.error(error);
              } finally {
                setLoading(false);
              }
            }}
          >
            Resetear
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        draggable: false,
        hideProgressBar: true,
      }
    );
  };

  // Toggle expandir/colapsar rol
  const toggleRoleExpansion = (role) => {
    setExpandedRoles(prev => ({
      ...prev,
      [role]: !prev[role]
    }));
  };

  // Group users by role
  const getUsersByRole = () => {
    const groupedUsers = {};
    
    filteredUsers.forEach(user => {
      if (!groupedUsers[user.role]) {
        groupedUsers[user.role] = [];
      }
      groupedUsers[user.role].push(user);
    });
    
    return groupedUsers;
  };

  // Manejar cambios en el formulario de nuevo usuario
  const handleNewUserInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  // Crear nuevo usuario
  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    try {
      setLoading(true);
      await createUser(newUser);
      toast.success('Usuario creado correctamente');
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      setShowNewUserModal(false);
      fetchRoles(); // Recargar roles y usuarios
    } catch (error) {
      toast.error('Error al crear el usuario');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-lg hover:shadow-2xl transition-all duration-300 mt-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium">Administración de Usuarios</CardTitle>
        <div className="flex space-x-2">
          <div className="relative">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Buscar usuarios..."
              className="pl-10 pr-4 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition-colors duration-200"
            />
            <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
          </div>
          <Button 
            onClick={() => fetchRoles()}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800"
            title="Refrescar lista"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button 
            onClick={() => setShowNewUserModal(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            <UserPlus className="h-4 w-4 mr-2" /> Nuevo Usuario
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center p-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {Object.entries(getUsersByRole()).length === 0 ? (
              <p className="text-center text-gray-500 py-10">
                No se encontraron usuarios que coincidan con la búsqueda
              </p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol Actual</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(getUsersByRole()).map(([role, roleUsers]) => (
                    <React.Fragment key={role}>
                      {/* Role header row */}
                      <tr className="bg-gray-100 cursor-pointer" onClick={() => toggleRoleExpansion(role)}>
                        <td colSpan="4" className="px-6 py-3 font-medium">
                          <div className="flex items-center">
                            {expandedRoles[role] ? (
                              <ChevronDown className="h-5 w-5 mr-2" />
                            ) : (
                              <ChevronRight className="h-5 w-5 mr-2" />
                            )}
                            Rol: <span className="font-bold ml-1">{role}</span> ({roleUsers.length} usuarios)
                          </div>
                        </td>
                      </tr>
                      
                      {/* User rows - only show if role is expanded */}
                      {expandedRoles[role] && roleUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">{user.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{user.role}</td>
                          <td className="px-6 py-4 whitespace-nowrap flex items-center">
                            <select
                              className="border rounded-md px-2 py-1 mr-2 text-sm"
                              value={user.role}
                              onChange={(e) => handleRoleUpdate(user.id, e.target.value)}
                            >
                              {/* Default essential roles */}
                              <option value="admin">Admin</option>
                              <option value="user">Usuario</option>
                              <option value="client">Cliente</option>
                              
                              {/* Dynamic roles from API */}
                              {availableRoles.map(role => (
                                // Only add roles that aren't already in default options
                                !["admin", "user", "client"].includes(role.name) && 
                                <option key={role.id} value={role.name}>{role.name}</option>
                              ))}
                            </select>
                            <button
                              className="text-red-500 hover:text-red-700 ml-2"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevenir propagación del evento
                                handleDeleteUser(user.id, user.name);
                              }}
                              disabled={loading}
                              title="Eliminar usuario"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                            <button
                              className="text-yellow-500 hover:text-yellow-700 ml-2"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevenir propagación del evento
                                handleResetPassword(user.id, user.name);
                              }}
                              title="Resetear contraseña"
                            >
                              <RefreshCcw className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Modal de Nuevo Usuario */}
        {showNewUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h3 className="text-lg font-medium mb-4">Crear Nuevo Usuario</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    type="text"
                    name="name"
                    value={newUser.name}
                    onChange={handleNewUserInputChange}
                    className="w-full p-2 border rounded-md"
                    placeholder="Nombre completo"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={newUser.email}
                    onChange={handleNewUserInputChange}
                    className="w-full p-2 border rounded-md"
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                  <input
                    type="password"
                    name="password"
                    value={newUser.password}
                    onChange={handleNewUserInputChange}
                    className="w-full p-2 border rounded-md"
                    placeholder="Contraseña segura"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                  <select
                    name="role"
                    value={newUser.role}
                    onChange={handleNewUserInputChange}
                    className="w-full p-2 border rounded-md"
                  >
                    {/* Default essential roles */}
                    <option value="admin">Admin</option>
                    <option value="user">Usuario</option>
                    <option value="client">Cliente</option>
                    
                    {/* Dynamic roles from API */}
                    {availableRoles.map(role => (
                      // Only add roles that aren't already in default options
                      !["admin", "user", "client"].includes(role.name) && 
                      <option key={role.id} value={role.name}>{role.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-2">
                <Button
                  onClick={() => setShowNewUserModal(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateUser}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  disabled={loading}
                >
                  Crear Usuario
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserAdministration;